/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { FC, MouseEventHandler } from "react";
import { styled } from "@mui/material/styles";
import { Box } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { BorderedIconButton } from "components/Common/BorderedIconButton";
import moment from "moment";
import { useTranslation } from "react-i18next";

interface TimeGutterHeaderProps {
  date: moment.Moment;
  onPrevClick?: MouseEventHandler<HTMLButtonElement>;
  onNextClick?: MouseEventHandler<HTMLButtonElement>;
}

export const TimeGutterHeader: FC<TimeGutterHeaderProps> = ({
  date,
  onPrevClick,
  onNextClick,
}) => {
  const { t } = useTranslation();
  return (
    <GutterHeader>
      <DateContainer>
        {moment().isSame(date, "day") ? (
          <TodayLabel>{t("AWVUI-MSG59")}</TodayLabel>
        ) : (
          <DateLabel>{date.format("DD/MM/YYYY")}</DateLabel>
        )}
      </DateContainer>
      <Buttons>
        <BorderedIconButton
          shape="square"
          onClick={onPrevClick}
          disabled={!onPrevClick}
        >
          <ArrowBackIosNewIcon />
        </BorderedIconButton>
        <BorderedIconButton
          shape="square"
          onClick={onNextClick}
          disabled={!onNextClick}
        >
          <ArrowForwardIosIcon />
        </BorderedIconButton>
      </Buttons>
    </GutterHeader>
  );
};

const GutterHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(0.5),
  width: "100%",
  height: "100%",
  backgroundColor: "#F3F3F3",
}));

const DateContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: `calc(${theme.typography.body1.fontSize} * 1.5)`,
}));

const TodayLabel = styled(Box)(({ theme }) => ({
  fontSize: theme.typography.body1.fontSize,
}));

const DateLabel = styled(Box)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
}));

const Buttons = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(0.5),
  button: {
    width: 36,
    height: 36,
    color: theme.palette.text.secondary,
    ".MuiSvgIcon-root": {
      width: 22,
      height: 22,
    },
  },
}));
